import { Box, Grid2 as Grid, Paper, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';
import { sum } from 'lodash';
import moment from 'moment';
import React, { Fragment, useContext } from 'react';
import { amountUiStr } from '../../utils/functions/common';
import { BookingStore } from '../../utils/stores/BookingStore';
import { ProductsStore } from '../../utils/stores/ProductsStore';

const summarySx = {
    paper: {
        padding: { xs: '15px', sm: '20px' },
        paddingBottom: { xs: '15px', sm: '8px' },
        borderRadius: '10px',
        ml: '10px',
    },
    detailContainer: {
        pl: '20px',
    },
    divider: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#D9DBE9',
        width: '100%',
        height: 0,
    },
    topDivider: {
        mt: { xs: '5px', md: '20px' },
    },
    itemText: {
        fontSize: 17,
        lineHeight: '110%',
        mb: '12px',
        mt: '12px',
        fontWeight: 300,
    },
    dateTimeText: {
        fontSize: 17,
        lineHeight: '110%',
        mb: '12px',
        fontWeight: 300,
    },
    qtyText: {
        fontSize: 15,
        lineHeight: '110%',
        fontWeight: 300,
        opacity: 0.65,
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        width: '100%',
    },
} as const;

interface SummaryProps {
    readonly isConfirmation?: boolean;
    /** Render only the pricing breakdown (fares, extras, total), skipping the
     *  title and date/time rows. Used where those details are shown elsewhere. */
    readonly pricingOnly?: boolean;
}

export function SummaryContent({ isConfirmation, pricingOnly }: SummaryProps) {
    const bookingController = useContext(BookingStore);
    const booking = isConfirmation ? bookingController.completedBookings.data.booking : bookingController.booking;
    const packageBooking = isConfirmation
        ? bookingController.completedBookings.data?.package
        : bookingController.packageBooking.data;
    const productController = useContext(ProductsStore);
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));

    return (
        <Box style={{ width: '100%' }}>
            {!isConfirmation && <Box sx={[summarySx.divider, summarySx.topDivider]} />}
            {!pricingOnly && (
                <>
                    <Box style={{ paddingTop: 10, paddingBottom: 10 }}>
                        {booking?.isPackage ? (
                            <Typography
                                variant="h6"
                                style={{
                                    paddingBottom: 0,
                                    paddingTop: 2,
                                    fontWeight: 400,
                                    fontSize: isConfirmation ? (isXs ? 18 : 20) : 19,
                                }}
                            >
                                {booking?.packageProductNames?.[0]}
                            </Typography>
                        ) : (
                            <Typography
                                variant="h6"
                                style={{
                                    paddingBottom: 0,
                                    fontWeight: 400,
                                    fontSize: isConfirmation ? (isXs ? 18 : 20) : 22,
                                }}
                            >
                                Booking Details
                            </Typography>
                        )}
                    </Box>
                    <Box sx={summarySx.row}>
                        <Typography sx={summarySx.dateTimeText}>Date of Travel</Typography>
                        <Typography sx={summarySx.dateTimeText}>
                            {moment(booking.tour.startTimeLocal).utcOffset(10).format('D MMM YYYY')}
                        </Typography>
                    </Box>
                    <Box sx={summarySx.row}>
                        <Typography sx={summarySx.dateTimeText}>Tour Time</Typography>
                        <Typography sx={summarySx.dateTimeText}>
                            {moment(booking.tour.startTimeLocal).utcOffset(10).format('h:mm a')}
                        </Typography>
                    </Box>
                    {booking?.isPackage && (
                        <>
                            <Typography
                                variant="h6"
                                style={{
                                    paddingBottom: 10,
                                    marginTop: 10,
                                    fontWeight: 400,
                                    fontSize: isConfirmation ? (isXs ? 18 : 20) : 19,
                                }}
                            >
                                {booking?.packageProductNames?.[1]}
                            </Typography>
                            <Box sx={summarySx.row}>
                                <Typography sx={summarySx.dateTimeText}>Date of Travel</Typography>
                                <Typography sx={summarySx.dateTimeText}>
                                    {isConfirmation
                                        ? moment(packageBooking.tour.startTimeLocal).utcOffset(10).format('D MMM YYYY')
                                        : !productController.availability.packageSessions.find((s) =>
                                                moment(s.startTime).isSame(packageBooking.tour.startTimeLocal)
                                            )
                                          ? 'TBA'
                                          : moment(packageBooking.tour.startTimeLocal)
                                                .utcOffset(10)
                                                .format('D MMM YYYY')}
                                </Typography>
                            </Box>
                            <Box sx={summarySx.row}>
                                <Typography sx={summarySx.dateTimeText}>Tour Time</Typography>
                                <Typography sx={summarySx.dateTimeText}>
                                    {isConfirmation
                                        ? moment(packageBooking.tour.startTimeLocal).utcOffset(10).format('h:mm a')
                                        : !productController.availability.packageSessions.find((s) =>
                                                moment(s.startTime).isSame(packageBooking.tour.startTimeLocal)
                                            )
                                          ? 'TBA'
                                          : moment(packageBooking.tour.startTimeLocal).utcOffset(10).format('h:mm a')}
                                </Typography>
                            </Box>
                            <Box sx={summarySx.divider} style={{ marginTop: 10, marginBottom: 0 }} />
                        </>
                    )}
                </>
            )}
            <Box style={{ paddingTop: 12 }}>
                <Typography variant="h6" style={{ fontWeight: 400, fontSize: 19 }}>
                    Fares
                </Typography>
            </Box>
            {booking?.quantities.map((item) => {
                if (item.value > 0) {
                    return (
                        <Box sx={summarySx.row} key={item.label}>
                            <Box style={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={summarySx.itemText} style={{ marginBottom: 3 }}>
                                    {item.label}
                                </Typography>
                                <Typography sx={summarySx.qtyText}>Quantity: {item.value}</Typography>
                                <Typography sx={summarySx.qtyText} style={{ marginTop: 3 }}>
                                    Price: ${item.price % 1 !== 0 ? amountUiStr(item.price) : item.price}
                                </Typography>
                            </Box>
                            <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Typography sx={summarySx.itemText}>
                                    ${' '}
                                    {(item.value * item.price) % 1 !== 0
                                        ? amountUiStr(item.value * item.price)
                                        : item.value * item.price}
                                </Typography>
                            </Box>
                        </Box>
                    );
                }
                return null;
            })}
            {booking.extras.length > 0 && sum(booking.extras.map((e) => e.quantity)) > 0 && (
                <>
                    <Box style={{ paddingTop: 12 }}>
                        <Typography variant="h6" style={{ fontWeight: 400, fontSize: 19 }}>
                            Extras
                        </Typography>
                    </Box>
                    {booking.extras.map((extra) => (
                        <Fragment key={extra.id}>
                            {extra.quantity > 0 && (
                                <Box sx={summarySx.row}>
                                    <Box style={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={summarySx.itemText} style={{ marginBottom: 3 }}>
                                            {extra.name}
                                        </Typography>
                                        <Typography sx={summarySx.qtyText}>Quantity: {extra.quantity}</Typography>
                                    </Box>
                                    <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Typography sx={summarySx.itemText}>
                                            ${' '}
                                            {((extra.price * extra.quantity) / 100) % 1 !== 0
                                                ? amountUiStr((extra.price * extra.quantity) / 100)
                                                : (extra.price * extra.quantity) / 100}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Fragment>
                    ))}
                </>
            )}
            <Box sx={summarySx.divider} style={{ marginTop: 22, marginBottom: 8 }} />
            <Box sx={summarySx.row}>
                <Typography sx={summarySx.itemText} style={{ fontSize: 18, fontWeight: 400 }}>
                    Total
                </Typography>
                <Typography sx={summarySx.itemText} style={{ fontSize: 18, fontWeight: 400 }}>
                    $ {amountUiStr(booking.totalAmount)}
                </Typography>
            </Box>
        </Box>
    );
}

export default function BookingFormSummary() {
    const productController = useContext(ProductsStore);

    return (
        <Paper sx={summarySx.paper} style={{ maxHeight: 760, overflowY: 'auto' }}>
            <Grid container>
                <Grid size={{ xs: 'auto' }}>
                    <img
                        src={productController.product.photos[0].thumbnailURL}
                        alt="product booking thumbnail"
                        style={{ objectFit: 'cover', height: 100, width: 100, borderRadius: 10 }}
                    />
                </Grid>
                <Grid size={{ xs: 'grow' }}>
                    <Grid container sx={summarySx.detailContainer}>
                        <Grid size={{ xs: 12 }}>
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 400, fontSize: 22, lineHeight: '115%', paddingBottom: 10 }}
                            >
                                {productController.product.name}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Typography sx={summarySx.itemText} style={{ fontSize: 16, marginBottom: 0, opacity: 0.9 }}>
                                {productController.product.tagline}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <SummaryContent />
        </Paper>
    );
}
